import React, { useState } from 'react';
import WomanRoundedIcon from '@mui/icons-material/WomanRounded';
import ManRoundedIcon from '@mui/icons-material/ManRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import './CancerChance.scss';

/**
 * 「香港患癌症風險 (0-74歲)」, an in-house replacement for the Piktochart of the same name,
 * in the look of ai.portfoplus.com: a new case every 14 minutes, the chance of getting cancer
 * and of dying of it as a row of people with one picked out, and the Piktochart's closing
 * "1 in 4" call to plan. The figures are the Hospital Authority's 2023 ones, which the English
 * and Simplified Chinese Piktocharts carry. A group-size slider turns the ratios into
 * "about how many people in a group like yours".
 */

type Sex = 'female' | 'male';

/** One in `oneIn` get it; the figures of the Hong Kong Cancer Registry, 2023. */
const CHANCE: Record<Sex, number> = { female: 5, male: 4 };
const DEATH: Record<Sex, number> = { female: 17, male: 11 };
const SEXES: Sex[] = ['female', 'male'];

const MINUTES = 14;
const CASES_2023 = '37,953';

const GROUP_MIN = 2;
const GROUP_MAX = 100;
const GROUP_DEFAULT = 4;
const GROUP_PRESETS = [4, 10, 30, 100];

const FACTSHEET = 'https://www3.ha.org.hk/cancereg/pdf/factsheet/2023/all_2023.pdf';
const OVERVIEW: Record<string, string> = {
  en: 'https://www3.ha.org.hk/cancereg/pdf/overview/Overview%20of%20HK%20Cancer%20Stat%202023.pdf'
};
const OVERVIEW_TC = 'https://www3.ha.org.hk/cancereg/pdf/overview/Overview%20of%20HK%20Cancer%20Stat%202023_tc.pdf';

/** About how many of `n` people, at one in `oneIn`, to one decimal and without a trailing ".0". */
export const expected = (n: number, oneIn: number) => String(Math.round((n / oneIn) * 10) / 10);

/** A person, in the flat style of the Piktochart: a skirt for women, a straight body for men. */
const Person = ({ sex, tone }: { sex: Sex; tone: 'plain' | 'case' | 'death' }) => (
  <svg className={`cc-person is-${tone}`} viewBox="0 0 24 56" aria-hidden="true">
    <circle cx="12" cy="6" r="5" />
    {sex === 'female' ? (
      <>
        <path d="M12 14c3.4 0 5.4 2 5.8 4.4L22 37H2l4.2-18.6C6.6 16 8.6 14 12 14z" />
        <rect x="7" y="35" width="4" height="19" rx="2" />
        <rect x="13" y="35" width="4" height="19" rx="2" />
      </>
    ) : (
      <>
        <rect x="3" y="14" width="18" height="22" rx="5" />
        <rect x="6" y="33" width="5" height="21" rx="2.5" />
        <rect x="13" y="33" width="5" height="21" rx="2.5" />
      </>
    )}
  </svg>
);

/** A row of `oneIn` people with the last one picked out: that is the "1 in N". */
const Crowd = ({ sex, oneIn, tone }: { sex: Sex; oneIn: number; tone: 'case' | 'death' }) => (
  <div className="cc-crowd" aria-hidden="true">
    {Array.from({ length: oneIn }, (_, i) => (
      <Person key={i} sex={sex} tone={i === oneIn - 1 ? tone : 'plain'} />
    ))}
  </div>
);

/** The stopwatch of the Piktochart, with a hand that keeps going round. */
const Stopwatch = () => (
  <svg className="cc-clock" viewBox="0 0 64 72" aria-hidden="true">
    <rect x="27" y="2" width="10" height="7" rx="2" />
    <circle className="cc-clock-face" cx="32" cy="40" r="26" />
    <g className="cc-clock-ticks">
      {[0, 90, 180, 270].map(deg => (
        <rect key={deg} x="31" y="17" width="2" height="5" transform={`rotate(${deg} 32 40)`} />
      ))}
    </g>
    <line className="cc-clock-hand" x1="32" y1="40" x2="32" y2="21" />
    <circle cx="32" cy="40" r="3" />
  </svg>
);

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const CancerChance = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [group, setGroup] = useState(GROUP_DEFAULT);

  /** A message with `{name}` slots, the values picked out in the highlight colour. */
  const rich = (id: string, slots: Record<string, string | number>, className: string) => {
    const marked = Object.keys(slots).reduce((acc, key) => ({ ...acc, [key]: `\u0001${slots[key]}\u0002` }), {});
    return t(id, marked)
      .split(/(\u0001[^\u0002]*\u0002)/)
      .map((part, i) =>
        part.startsWith('\u0001') ? (
          <strong key={i} className={className}>
            {part.slice(1, -1)}
          </strong>
        ) : (
          part
        )
      );
  };

  const ratio = (oneIn: number) => t('cc-ratio', { n: oneIn });
  const overview = OVERVIEW[intl.locale] || OVERVIEW_TC;

  return (
    <div className="cancer-chance" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="cc-head">
        <h2 className="cc-title">
          <span>{t('cc-title')}</span>
        </h2>
        <p className="cc-clock-line">{rich('cc-clock-line', { m: MINUTES, one: 1 }, 'cc-num')}</p>
        <Stopwatch />
      </header>

      {[
        { key: 'chance', data: CHANCE, tone: 'case' as const },
        { key: 'death', data: DEATH, tone: 'death' as const }
      ].map(({ key, data, tone }) => (
        <section key={key} className={`cc-block is-${key}`}>
          <h3>{t(`cc-${key}-head`)}</h3>
          {SEXES.map(sex => (
            <div key={sex} className={`cc-row is-${sex}`}>
              <span className="cc-who">
                <span className="cc-avatar" aria-hidden="true">
                  {sex === 'female' ? <WomanRoundedIcon /> : <ManRoundedIcon />}
                </span>
                {t(`cc-${sex}`)}
              </span>
              <Crowd sex={sex} oneIn={data[sex]} tone={tone} />
              <strong className="cc-ratio">{ratio(data[sex])}</strong>
            </div>
          ))}
        </section>
      ))}

      <section className="cc-try" aria-live="polite">
        <label htmlFor="cc-group">
          <span>
            <GroupsRoundedIcon aria-hidden="true" />
            {t('cc-try')}
          </span>
          <strong>{t('cc-group', { n: group })}</strong>
        </label>
        <input
          id="cc-group"
          type="range"
          min={GROUP_MIN}
          max={GROUP_MAX}
          value={group}
          onChange={event => setGroup(Number(event.target.value))}
          style={{ '--fill': `${((group - GROUP_MIN) / (GROUP_MAX - GROUP_MIN)) * 100}%` } as React.CSSProperties}
        />
        <div className="cc-presets">
          {GROUP_PRESETS.map(n => (
            <button
              key={n}
              type="button"
              className={n === group ? 'is-on' : ''}
              aria-pressed={n === group}
              onClick={() => setGroup(n)}
            >
              {t('cc-group', { n })}
            </button>
          ))}
        </div>
        <div className="cc-results">
          {SEXES.map(sex => (
            <div key={sex} className={`cc-result is-${sex}`}>
              <span className="cc-who">
                <span className="cc-avatar" aria-hidden="true">
                  {sex === 'female' ? <WomanRoundedIcon /> : <ManRoundedIcon />}
                </span>
                {t(`cc-${sex}`)}
              </span>
              <p>{rich('cc-res-cases', { k: expected(group, CHANCE[sex]) }, 'cc-case')}</p>
              <p>{rich('cc-res-deaths', { d: expected(group, DEATH[sex]) }, 'cc-death')}</p>
            </div>
          ))}
        </div>
        <small>{t('cc-try-note')}</small>
      </section>

      <div className="cc-tip">
        <CampaignRoundedIcon aria-hidden="true" />
        <p>{t('cc-conclusion')}</p>
      </div>

      <p className="cc-source">
        <strong>{t('cc-source')}</strong>
        <a href={FACTSHEET} target="_blank" rel="noopener noreferrer">
          {t('cc-source-1')}
        </a>
        <a href={overview} target="_blank" rel="noopener noreferrer">
          {t('cc-source-2')}
        </a>
        <small>{t('cc-footnote', { cases: CASES_2023, m: MINUTES })}</small>
      </p>
    </div>
  );
};

export default CancerChance;
